import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type UpdatePhase =
  | 'idle'
  | 'checking'
  | 'downloading'
  | 'installing'
  | 'restarting'
  | 'error';

type PwaUpdateContextValue = {
  updateAvailable: boolean;
  phase: UpdatePhase;
  progress: number;
  error: string | null;
  checkForUpdate: () => Promise<boolean>;
  applyUpdate: () => Promise<void>;
};

/** Menovky fáz. Pri hooku, nie pri Nastaveniach: to isté potrebuje aj
 *  obrazovka zastaranej verzie, ktorá musí byť v hlavnom balíku. */
export const UPDATE_PHASE_LABEL: Record<UpdatePhase, string> = {
  idle: 'Pripravené',
  checking: 'Kontrolujem aktualizáciu…',
  downloading: 'Sťahujem…',
  installing: 'Inštalujem…',
  restarting: 'Spúšťam novú verziu…',
  error: 'Aktualizácia sa zastavila',
};

const PwaUpdateContext = createContext<PwaUpdateContextValue | null>(null);

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => window.setTimeout(resolve, ms));

const waitForWaitingWorker = async (
  registration: ServiceWorkerRegistration,
  timeoutMs = 15_000,
): Promise<ServiceWorker | null> => {
  if (registration.waiting) return registration.waiting;

  return new Promise((resolve) => {
    let settled = false;
    const finish = (worker: ServiceWorker | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      resolve(worker);
    };
    const watch = (worker: ServiceWorker | null) => {
      if (!worker) return;
      if (worker.state === 'installed') {
        finish(registration.waiting ?? worker);
        return;
      }
      worker.addEventListener('statechange', () => {
        if (worker.state === 'installed') finish(registration.waiting ?? worker);
        if (worker.state === 'redundant') finish(null);
      });
    };
    const timeout = window.setTimeout(() => finish(registration.waiting), timeoutMs);
    watch(registration.installing);
    registration.addEventListener('updatefound', () => watch(registration.installing), {
      once: true,
    });
  });
};

/**
 * One update controller for the whole app. A new worker stays waiting until an
 * explicit install, so every open tab continues to use the old bundle. The
 * visible progress is intentional product choreography around the browser's
 * atomic worker hand-off; controllerchange is the only normal reload path.
 */
export const PwaUpdateProvider = ({ children }: { children: ReactNode }) => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [phase, setPhase] = useState<UpdatePhase>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const regRef = useRef<ServiceWorkerRegistration | null>(null);
  const reloadingRef = useRef(false);
  const installRequestedRef = useRef(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const controlledAtMount = Boolean(navigator.serviceWorker.controller);
    let cancelled = false;
    let checkTimer = 0;
    let registration: ServiceWorkerRegistration | null = null;
    let onVisible: (() => void) | null = null;
    let onUpdateFound: (() => void) | null = null;

    const onControllerChange = () => {
      // First-ever installation has no old app to replace. For an established
      // app, however, controllerchange can also be triggered by another tab.
      // Reload every established tab so none keeps a stale update badge after
      // the user already approved the update elsewhere.
      if ((!installRequestedRef.current && !controlledAtMount) || reloadingRef.current) return;
      reloadingRef.current = true;
      setUpdateAvailable(false);
      setPhase('restarting');
      setProgress(100);
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    void navigator.serviceWorker.ready.then((reg) => {
      if (cancelled) return;
      registration = reg;
      regRef.current = reg;
      if (reg.waiting && navigator.serviceWorker.controller) setUpdateAvailable(true);

      const check = () => void reg.update().catch(() => undefined);
      onVisible = () => {
        if (!document.hidden) check();
      };
      document.addEventListener('visibilitychange', onVisible);
      checkTimer = window.setInterval(check, 60 * 60_000);

      onUpdateFound = () => {
        const installing = reg.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            regRef.current = reg;
            setUpdateAvailable(true);
          }
        });
      };
      reg.addEventListener('updatefound', onUpdateFound);
      check();
    });

    return () => {
      cancelled = true;
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      if (onVisible) document.removeEventListener('visibilitychange', onVisible);
      if (registration && onUpdateFound) {
        registration.removeEventListener('updatefound', onUpdateFound);
      }
      if (checkTimer) window.clearInterval(checkTimer);
    };
  }, []);

  const registration = async (): Promise<ServiceWorkerRegistration> => {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Tento prehliadač nepodporuje aktualizácie aplikácie.');
    }
    const reg = regRef.current ?? (await navigator.serviceWorker.ready);
    regRef.current = reg;
    return reg;
  };

  const checkForUpdate = async (): Promise<boolean> => {
    setError(null);
    setPhase('checking');
    setProgress(2);
    try {
      const reg = await registration();
      await reg.update();
      const worker = reg.waiting ?? (await waitForWaitingWorker(reg, 5_000));
      const available = Boolean(worker && navigator.serviceWorker.controller);
      setUpdateAvailable(available);
      setProgress(available ? 6 : 0);
      setPhase('idle');
      return available;
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : 'Kontrola aktualizácie zlyhala.';
      setError(message);
      setPhase('error');
      return false;
    }
  };

  const animateProgress = async (from: number, to: number, duration: number) => {
    const started = performance.now();
    setProgress(from);
    while (performance.now() - started < duration) {
      const ratio = Math.min(1, (performance.now() - started) / duration);
      const eased = 1 - (1 - ratio) ** 3;
      setProgress(Math.round(from + (to - from) * eased));
      await wait(32);
    }
    setProgress(to);
  };

  const applyUpdate = async (): Promise<void> => {
    if (['downloading', 'installing', 'restarting'].includes(phase)) return;
    setError(null);

    try {
      const reg = await registration();
      let worker = reg.waiting;
      if (!worker) {
        setPhase('checking');
        setProgress(3);
        await reg.update();
        worker = await waitForWaitingWorker(reg);
      }
      if (!worker || !navigator.serviceWorker.controller) {
        throw new Error('Aktualizačný balík ešte nie je dostupný. Skús kontrolu znova o chvíľu.');
      }

      setUpdateAvailable(true);
      setPhase('downloading');
      await animateProgress(6, 68, 1_250);
      setPhase('installing');
      await animateProgress(68, 96, 900);
      setPhase('restarting');
      setProgress(100);
      await wait(260);

      installRequestedRef.current = true;
      worker.postMessage({ type: 'SKIP_WAITING' });

      // A controllerchange event normally reloads immediately. This is only a
      // recovery for a browser that activated the worker but swallowed it.
      window.setTimeout(() => {
        if (!reloadingRef.current) window.location.reload();
      }, 5_000);
    } catch (cause) {
      const message =
        cause instanceof Error ? cause.message : 'Aktualizáciu sa nepodarilo pripraviť.';
      setError(message);
      setPhase('error');
      setProgress(0);
    }
  };

  return createElement(
    PwaUpdateContext.Provider,
    { value: { updateAvailable, phase, progress, error, checkForUpdate, applyUpdate } },
    children,
  );
};

export const usePwaUpdate = (): PwaUpdateContextValue => {
  const context = useContext(PwaUpdateContext);
  if (!context) throw new Error('usePwaUpdate must be used within PwaUpdateProvider');
  return context;
};
